<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/programme"
            >
              <span class="brand-logo">
                <b-img
                  :src="logo"
                  alt="logo"
                />
              </span>
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <div
        v-show="!isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered)"
        class="sidebar-menu-search p-1"
      >
        <b-input-group class="input-group-merge">
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            :value="searchQuery"
            placeholder="Tìm kiếm menu..."
            @input="val => searchQuery = val"
          />
        </b-input-group>
      </div>

      <vertical-nav-menu-items
        :items="filteredMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import {
  BLink, BImg, BFormInput, BInputGroup, BInputGroupPrepend,
} from 'bootstrap-vue'
import {
  provide,
  computed,
  ref,
  onMounted,
} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import store from '@/store'
import { isUserLoggedIn } from '@/auth/utils'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import { getMessaging, getToken } from 'firebase/messaging'
import { VAPID_KEY } from '@/const/firebase'
import { OSType } from '@/const/type'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const requestPermission = async () => {
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        const messaging = getMessaging()
        const token = await getToken(messaging, { vapidKey: VAPID_KEY })
        await store.dispatch('accountFcmToken/createAccountFcmToken', { code: token, osType: OSType.WEB })
      }
    }

    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    // App Name
    const { appLogoImage } = $themeConfig.app

    const navMenuItems = ref([])
    const logo = ref(null)

    const { t } = useI18nUtils()
    const searchQuery = ref('')
    provide('navMenuSearchQuery', searchQuery)

    const filteredMenuItems = computed(() => {
      if (!searchQuery.value) return navMenuItems.value

      const q = searchQuery.value.toLowerCase()

      const filterItem = item => {
        const title = t(item.title) || item.title || ''
        const isMatch = title.toLowerCase().includes(q)

        let children = []
        if (item.children) {
          children = item.children.map(filterItem).filter(Boolean)
        }

        if (isMatch || children.length > 0) {
          return {
            ...item,
            ...(children.length > 0 ? { children } : {}),
          }
        }
        return null
      }

      return navMenuItems.value.map(filterItem).filter(Boolean)
    })

    onMounted(async () => {
      if (isUserLoggedIn()) {
        await store.dispatch('menu/getNavMenuItems')
        navMenuItems.value = store.getters['menu/navMenuItems']
        await requestPermission()
      }
      logo.value = store.state.app.organizationProperty.logo || appLogoImage
    })

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    return {
      filteredMenuItems,
      searchQuery,
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      logo,

      requestPermission,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>
